// src/components/LuxuryRoom.jsx
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import Mannequin from "./Mannequin";
import { useDepthTexture } from "./DepthOcclusionProvider";
import { useGLTF, PerspectiveCamera, Environment } from "@react-three/drei";

const ASSET_BASE =
  process.env.REACT_APP_ASSET_BASE_URL ||
  "https://pub-f0f38b8dd0224e2f89367893cf6bb0d7.r2.dev";

const FITTING_ROOM_GLB_URL = `${ASSET_BASE}/scenes/fitting_room.glb`;

export default function LuxuryRoom({
  avatarParams,
  selectedItems,
  garmentMetricsMap = null,
  bodyRefs = null,
  view = "front",
  onToggleView,
}) {


  const frontCamRef = useRef(null);
const backCamRef = useRef(null);
// ---- camera tuning (tight room, full body + sign) ----
const CAM_FOV = 38;          // wider, showroom lens
const CAM_Y = 1.55;          // lifts sign into frame
const CAM_DIST = 4.0;        // safely inside walls
const CAM_TARGET = [0, 1.05, 0]; // chest anchor

  const { scene } = useGLTF(FITTING_ROOM_GLB_URL);
  const mannequinRootRef = useRef(null);
  
  const depthTexture = useDepthTexture();
    
  useEffect(() => {
    if (!scene) return;
    scene.traverse((obj) => {
  if (obj.isMesh) {
    obj.castShadow = true;
    obj.receiveShadow = true;

    // ✅ HIDE any mannequin/body meshes that are baked into fitting_room.glb
    const n = (obj.name || "").toLowerCase();
    if (
      n.includes("mannequin") ||
      n.includes("avatar") ||
      n.includes("body") ||
      n.includes("human") ||
      n.includes("character")
    ) {
      obj.visible = false;
    }
  }

  // hide baked lights
  if (obj.isLight) obj.visible = false;
});

  }, [scene]);

  useEffect(() => {
  const cam = view === "back" ? backCamRef.current : frontCamRef.current;
  if (!cam) return;
  cam.lookAt(...CAM_TARGET);
  cam.updateMatrixWorld();
  cam.updateProjectionMatrix();
}, [view]);

  
//useEffect(() => {
  //if (!mannequinRootRef.current) return;
  //mannequinRootRef.current.rotation.y = backView ? Math.PI : 0;
//}, [backView]);

const CURTAIN_SPOTS = useMemo(() => {
  const N = 20;
  const rLight = 4.6;
  const rTarget = 4.0;
  const yLight = 2.9;
  const yTarget = 2.5;

  return Array.from({ length: N }, (_, i) => {
    const a = (i / N) * Math.PI * 2;
    return {
      pos: [
        Math.cos(a) * rLight,
        yLight,
        Math.sin(a) * rLight,
      ],
      target: [
        Math.cos(a) * rTarget,
        yTarget,
        Math.sin(a) * rTarget,
      ],
    };
  });
}, []);

// Force Mannequin to remount when the "active try-on item" changes.
// This guarantees pose state resets between try-ons and when clothes are removed.
const mannequinKey = useMemo(() => {
  const active =
    selectedItems?.dress ||
    selectedItems?.top ||
    selectedItems?.bottom ||
    selectedItems?.outer ||
    null;

  // pick something stable and unique per item
  return `mnq-${active?.id ?? active?.meshUrl ?? active?.name ?? "none"}`;
}, [selectedItems]);

  return (
    <>
  
<PerspectiveCamera
  ref={frontCamRef}
  makeDefault={view === "front"}
  fov={CAM_FOV}
  position={[0, CAM_Y, CAM_DIST]}
  near={0.1}
  far={100}
/>

<PerspectiveCamera
  ref={backCamRef}
  makeDefault={view === "back"}
  fov={CAM_FOV}
  position={[0, CAM_Y, -CAM_DIST]}
  near={0.1}
  far={100}
/>
{/* ---------- Balanced boutique lighting (bright, not blown) ---------- */}
{/* Environment = global bounce, keep LOW */}
<Environment preset="city" intensity={0.2} />

{/* =========================
   NATURAL BOUTIQUE LIGHTING
   (directional, inviting, not symmetric)
   ========================= */}

/** Base lift (keeps it “open” not abandoned) */
<ambientLight intensity={0.18} color="#fbf2ea" />


/** KEY: one believable ceiling spotlight, slightly forward + offset
    (this is what makes it feel real) */
<spotLight
  position={[0.9, 6.8, 2.2]}
  angle={0.55}
  penumbra={1.0}
  intensity={5}
  distance={24}
  decay={2}
  color="#fff3ea"
  castShadow
  shadow-mapSize-width={2048}
  shadow-mapSize-height={2048}
  shadow-bias={-0.00015}
/>
<directionalLight
  position={[4, 6, 2]}   // pretend window from the right
  intensity={1.3}
  color="#fff6ec"
  castShadow
  shadow-mapSize-width={2048}
  shadow-mapSize-height={2048}
  shadow-bias={-0.00015}
/>


/** RIM: tiny separation so mannequin doesn’t look pasted on */
<spotLight
  position={[-1.8, 4.4, -2.4]}
  angle={0.9}
  penumbra={1.0}
  intensity={0.9}
  distance={18}
  decay={2}
  color="#ffffff"
  castShadow={false}
/>



      <Suspense fallback={null}>
        <primitive object={scene} />

       <group
  ref={mannequinRootRef}
  position={[0, 0, 0]}
  rotation={[0, 0, 0]}
>
<Mannequin
  key={mannequinKey}
  avatarParams={avatarParams}
  selectedItems={selectedItems}
  view={view}
  depthTexture={depthTexture} // ✅ depth ON
  autoRotate={false}
  garmentMetricsMap={garmentMetricsMap}
  bodyRefs={bodyRefs}
/>

</group>

           </Suspense>
   </>

  );
}

useGLTF.preload(FITTING_ROOM_GLB_URL);
